<script setup>
  import { onMounted, ref } from "vue";
  import Lucide from "../../base-components/Lucide";
  import logoUrl from "/image/logos/logo.200.png";
  import Breadcrumb from "../../base-components/Breadcrumb";
  import { Menu } from "../../base-components/Headless";
  import { useLogout } from "@/composables/useLogout.js";
  import { useUserStore } from "@/stores/user";
  import { useRoute } from "vue-router";
  import { localeOptions, uri } from "@/constants/config.js";
  import WalletDialog from "./WalletDialog.vue";
  import { useNumberFormat } from "@/composables/useNumberFormat.js";
  import { getDirection } from "@/utils/index.js";
  import { useLocaleStore } from "@/stores/locale.js";
  import { useWalletStore } from "@/stores/walletStore.js";
  import BaseButton from "@/base-components/Button";
  import Button from "@/base-components/Button";
  import { Dialog } from "@/base-components/Headless";
  import { useFetch } from "@/composables/useFetch.js";
  import PromotionDialog from "@/components/TopBar/PromotionDialog.vue";

  const userStore = useUserStore();
  const walletStore = useWalletStore();
  const { formatNumber } = useNumberFormat();

  const route = useRoute();
  const id = ref(route.params.id);
  const loadingFlag = ref(false);

  async function getWallet() {
    loadingFlag.value = true;
    await walletStore.fetchWallet();
    loadingFlag.value = false;
  }

  getWallet();

  const localeStore = useLocaleStore();
  function changeLanguage(data) {
    const { id: locale, direction } = data;
    const currentDirection = getDirection().direction;
    if (direction !== currentDirection) {
      localeStore.changeDirection(direction);
    }
    localeStore.changeLang(locale);
  }

  const showPromotionDialog = ref(false);
  let promotion = ref(null);

  function closePromotionDialog() {
    userStore.setIsPromotionChecked(true);
    showPromotionDialog.value = false;
    promotion.value = null;
    getWallet();
  }

  onMounted(async () => {
    const isPromotionChecked = userStore.getIsPromotionChecked();
    if (!isPromotionChecked) {
      const { data } = await useFetch(uri.api.promotion.list, undefined, false);
      promotion.value = data.data.pop();
      showPromotionDialog.value = true;
    }
  });
</script>

<template>
  <PromotionDialog
    v-if="promotion && showPromotionDialog"
    :is-open="showPromotionDialog"
    :promotion="promotion"
    @close="closePromotionDialog"
  />
  <Dialog
    :open="walletStore.showNotification"
    @close="walletStore.closeNotification"
  >
    <Dialog.Panel>
      <div class="p-5 text-center">
        <Lucide icon="XCircle" class="w-16 h-16 mx-auto mt-3 text-warning" />
        <div class="mt-5 text-3xl">موجودی کافی نیست</div>
        <div class="mt-2 text-slate-500">
          جهت تکمیل عملیات کیف پول خود را شارژ نمایید!
        </div>
      </div>
      <div class="px-5 pb-8 text-center">
        <Button
          type="button"
          variant="primary"
          @click="
            () => {
              walletStore.closeNotification();
              walletStore.openWallet();
            }
          "
          class="w-24"
        >
          کیف پول
        </Button>
      </div>
    </Dialog.Panel>
  </Dialog>
  <section>
    <div v-if="walletStore.showWalletDialog">
      <WalletDialog
        @close="walletStore.closeWallet()"
        @onSuccess="getWallet"
        :show="walletStore.showWalletDialog"
      />
    </div>
    <!-- BEGIN: Top Bar -->
    <div
      class="top-bar-boxed h-[70px] z-[51] relative border-b border-white/[0.08] mt-12 md:-mt-5 -mx-3 sm:-mx-8 px-3 sm:px-8 md:pt-0 mb-12"
    >
      <div class="flex items-center h-full">
        <!-- BEGIN: Logo -->
        <RouterLink
          :to="{ name: 'side-menu-dashboard-overview-1' }"
          class="hidden -intro-x md:flex"
        >
          <img alt="DezhCloud" class="w-6" :src="logoUrl" />
          <span class="ms-3 text-lg text-white">
            {{ $t("title.web-title") }}
          </span>
        </RouterLink>
        <!-- END: Logo -->
        <!-- BEGIN: Breadcrumb -->
        <Breadcrumb
          light
          class="h-full md:ms-10 md:ps-10 md:border-l border-white/[0.08] me-auto -intro-x"
        >
          <Breadcrumb.Link to="/"></Breadcrumb.Link>
          <Breadcrumb.Link to="/" :active="true"></Breadcrumb.Link>
        </Breadcrumb>
        <!-- END: Breadcrumb -->

        <!-- BEGIN: Wallet -->
        <div
          class="me-4 intro-x sm:me-6 flex gap-3 cursor-pointer"
          v-if="!loadingFlag && walletStore.wallet"
          @click="walletStore.openWallet"
        >
          <div class="dark:text-slate-500 mt-2 text-white flex gap-1">
            <div class="relative lg:text-xl md:text-xl font-bold">
              {{ formatNumber(walletStore.wallet.amount) }}
            </div>
            <div class="pt-1">
              {{ $t("title.currency-unit") }}
            </div>
          </div>
          <div class="relative text-white/70 outline-none block">
            <Lucide
              icon="CreditCard"
              class="w-7 h-7 dark:text-slate-500 mt-1"
            />
          </div>
        </div>
        <!-- END: Wallet -->
        <!-- BEGIN: Notifications -->
        <div class="me-4 intro-x sm:me-6">
          <div
            class="relative text-white/70 outline-none block before:content-[''] before:w-[8px] before:h-[8px] before:rounded-full before:absolute before:top-[6px] before:start-1 before:bg-danger"
          >
            <Lucide icon="Bell" class="w-7 h-7 dark:text-slate-500 mt-1" />
          </div>
        </div>
        <!-- END: Notifications -->
        <!-- BEGIN: Account Menu -->
        <Menu>
          <Menu.Button class="relative text-white/70 outline-none block">
            <Lucide icon="User" class="w-8 h-8 dark:text-slate-500" />
          </Menu.Button>
          <Menu.Items
            class="w-56 mt-px relative bg-primary/80 before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white"
          >
            <Menu.Header class="font-normal">
              <div class="font-medium">
                {{
                  userStore.currentUser ? userStore.currentUser.firstName : ""
                }}
              </div>
              <div class="text-xs text-white/70 mt-0.5 dark:text-slate-500">
                {{ userStore.currentUser ? userStore.currentUser.email : "" }}
              </div>
            </Menu.Header>
            <Menu.Divider class="bg-white/[0.08]" />
            <RouterLink to="/account">
              <Menu.Item class="hover:bg-white/5">
                <Lucide icon="User" class="w-4 h-4 me-2" />
                {{ $t("menu.profile") }}
              </Menu.Item>
            </RouterLink>
            <Menu.Divider class="bg-white/[0.08]" />
            <Menu.Item class="hover:bg-white/5" @click="useLogout">
              <Lucide icon="ToggleRight" class="w-4 h-4 me-2" />
              {{ $t("menu.logout") }}
            </Menu.Item>
          </Menu.Items>
        </Menu>
        <!-- END: Account Menu -->
        <!-- BEGIN: Language Menu -->
        <Menu>
          <Menu.Button class="relative text-white/70 outline-none block">
            <BaseButton
              variant="outline-secondary"
              size="sm"
              class="mx-3 text-white font-bold text-base"
            >
              {{ $i18n.locale.toUpperCase() }}
            </BaseButton>
          </Menu.Button>
          <Menu.Items
            class="w-24 mt-px relative bg-primary/80 before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white"
          >
            <Menu.Item
              class="hover:bg-white/5"
              v-for="lang in localeOptions"
              :key="lang.id"
              @click="changeLanguage(lang)"
            >
              <Lucide icon="Languages" class="w-4 h-4 me-2" />
              {{ lang.name }}
            </Menu.Item>
          </Menu.Items>
        </Menu>
        <!-- END: Language Menu -->
      </div>
    </div>
    <!-- END: Top Bar -->
  </section>
</template>
